import {
  DoRequestResponse,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  ImmobilieUebernehmenPayload,
  VorbelegungPayload,
} from '@ps20-frontend/shop-integration-redux';
import { call, cancel, put, select, take, takeEvery } from 'redux-saga/effects';
import {
  App,
  BasisAdresse,
  isBasisAdresseExist,
  mapVorbelegungdaten,
  VhvV2Vorbelegungdaten,
  Vorbelegungdaten,
} from '@ps20-frontend/frontend-models';
import {
  SpartenType,
  VertragsdatenRequestModel,
  VertragsVorbelegungdaten,
  VhvV2Vorvertragsdaten,
} from '@ps20/bff-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { equalAdressen } from '@ps20-frontend/vorschlagsdaten-lib';
import { VhvActions } from '../vhvActions';
import { IAppState } from '../appState';

export enum VorbelegungVhvRequestEventsEnum {
  VORBELEGUNG_VHV_REQUEST_SUCCESSFULL = 'VORBELEGUNG_VHV_REQUEST_SUCCESSFULL',
  VORBELEGUNG_VHV_REQUEST_FAILED = 'VORBELEGUNG_VHV_REQUEST_FAILED',
}

export function* watchVorbelegungRequested() {
  yield takeEvery(GlobalActions.VORBELEGUNG_REQUESTED, safeSaga(vorbelegungSaga, App.VHV_V2));
  yield takeEvery(
    VorbelegungVhvRequestEventsEnum.VORBELEGUNG_VHV_REQUEST_SUCCESSFULL,
    safeSaga(successCallback, App.VHV_V2),
  );
  yield takeEvery(
    VorbelegungVhvRequestEventsEnum.VORBELEGUNG_VHV_REQUEST_FAILED,
    safeSaga(errorCallback, App.VHV_V2),
  );
  yield takeEvery(GlobalActions.VORBELEGUNG_FINISHED, safeSaga(zusatzoptionenVorbelegung, App.VHV_V2));
}

const globalActions = new GlobalActions();

export function* vorbelegungSaga(action: GlobalAction<VorbelegungPayload>) {
  const state: IAppState = yield select();
  if (action.payload.appId !== App.VHV_V2 || action.payload.angebotsId !== state.angebotsId) {
    return;
  }

  if (action.payload?.vorhandeneVorbelegungdaten) {
    yield* vhvDatenVorbelegen(action.payload.vorhandeneVorbelegungdaten);
  } else {
    const globalPut = GlobalPut.getInstance();
    const vertragsdatenRequestModel: VertragsdatenRequestModel = {
      spLaufnummer: action.payload.spLaufnummer,
      vertragsnummer: action.payload.vertragsnummer,
    };
    yield call(
      [globalPut, globalPut.put],
      globalActions.doRequest({
        appId: App.VHV_V2,
        body: vertragsdatenRequestModel,
        successEvent: VorbelegungVhvRequestEventsEnum.VORBELEGUNG_VHV_REQUEST_SUCCESSFULL,
        errorEvent: VorbelegungVhvRequestEventsEnum.VORBELEGUNG_VHV_REQUEST_FAILED,
        url: 'privatschutz/api/vertragsdaten',
      }),
    );
  }
}

export function* successCallback(successAction: DoRequestResponse<VhvV2Vorvertragsdaten>) {
  const vhvVorbelegungdaten: VhvV2Vorbelegungdaten = getVHVVorbelegungdaten(successAction.payload);
  yield* vhvDatenVorbelegen(vhvVorbelegungdaten);
}

const vhvActions = new VhvActions();

export function* vhvDatenVorbelegen(vorbelegungdaten: Vorbelegungdaten) {
  const state: IAppState = yield select();
  let beibehalten = false;
  const vhvVorbelegungdaten: VhvV2Vorbelegungdaten = vorbelegungdaten as VhvV2Vorbelegungdaten;
  const globalPut = GlobalPut.getInstance();

  yield put(vhvActions.familienstandChanged(vhvVorbelegungdaten.familienstand));

  if (!state.produkt?.produktlinien) {
    yield take(GlobalActions.PRODUKT_RECEIVED);
  }
  if (vorbelegungdaten.internerVertragsbezugs) {
    yield put(
      globalActions.internerVertragsbezugReceived(
        vorbelegungdaten.internerVertragsbezugs,
        App.VHV_V2,
        state.angebotsId,
      ),
    );
  }
  if (state.vertragsoptionen?.beibehalten) {
    vhvVorbelegungdaten.angebotsId = state.angebotsId;
    yield call(
      [globalPut, globalPut.put],
      globalActions.vorbelegungForBeibehaltungRequested(vhvVorbelegungdaten),
    );
    if (!vhvVorbelegungdaten.wertsachensumme) {
      yield take(VhvActions.WERTSACHENSUMMEN_CHANGED_AFTER_VORBELEGUNG);
    }
    beibehalten = true;
  }
  yield call(
    globalPut.put,
    globalActions.vorbelegungFinished({
      appId: App.VHV_V2,
      vorhandeneVorbelegungdaten: vhvVorbelegungdaten,
      angebotsId: state.angebotsId,
      beibehalten,
    }),
  );
}

export function* errorCallback() {
  const globalPut = GlobalPut.getInstance();
  yield call([globalPut, globalPut.put], globalActions.vorbelegungFinished({ appId: App.VHV_V2 }));
  yield cancel();
}

export function* zusatzoptionenVorbelegung(action: GlobalAction<VorbelegungPayload>) {
  const state: IAppState = yield select();
  if (action.payload.appId !== App.VHV_V2 || action.payload.angebotsId !== state.angebotsId) {
    return;
  }
  if (!action.payload.beibehalten) {
    let selectedRisikoort: BasisAdresse;

    if (
      state.vorbelegungen.adresseImmobilieVorbelegt === false ||
      !isBasisAdresseExist(state.risikoort as BasisAdresse)
    ) {
      const immobilieAction: GlobalAction<ImmobilieUebernehmenPayload> = yield take(
        GlobalActions.IMMOBILIE_UEBERNEHMEN,
      );
      selectedRisikoort = immobilieAction.payload.immobilie?.risikoort as BasisAdresse;
    } else {
      selectedRisikoort = state.risikoort as BasisAdresse;
    }

    const vhvVorbelegungdaten: VhvV2Vorbelegungdaten =
      action.payload.vorhandeneVorbelegungdaten as VhvV2Vorbelegungdaten;
    if (equalAdressen(selectedRisikoort, vhvVorbelegungdaten.risikoort as BasisAdresse)) {
      yield put(vhvActions.produktZusatzoptionenVorbelegt(vhvVorbelegungdaten));
      yield put(vhvActions.requestPreis());
    }
  }
}

function getVHVVorbelegungdaten(
  bffVorbelegungdaten: VertragsVorbelegungdaten,
): VhvV2Vorbelegungdaten {
  return mapVorbelegungdaten(SpartenType.HAUSRAT_V2, bffVorbelegungdaten) as VhvV2Vorbelegungdaten;
}
